// Filter bar: project/context filter chips derived from task data, with counts.
// Chips are wired to TasksContext (projectFilter/contextFilter) so every page that
// reads filteredTasks filters along. OR within a category, AND between categories.
// Counts are scoped to the current page (Today: open work; Plan: unscheduled +
// next 3 days; Organize: active root tasks; Dashboard: waiting + completed on task.date), not the
// full task database, and ignore the active chip filters so they stay stable.
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Box, Chip } from '@mui/material';
import {
  renderContextFilterChip,
  renderFilterBarTagDivider,
  renderProjectFilterChip,
} from '@/components/layout/filterBarChips';
import { useLocation } from 'react-router-dom';
import { useTasksContext } from '@/features/tasks';
import {
  getTaskProjects,
  getTaskContexts,
} from '@/features/tasks/utils/data/taskTags';
import {
  buildTagOptions,
  buildSelectedFilterSets,
  ensureSelectedOptions,
  getViewFromPath,
  getViewScopedTasks,
  HIGHLIGHT_PROJECT_FILTER_EVENT,
  toggleFilterById,
} from '@/components/layout/filterBarUtils';
import { useT } from '@/i18n/useT';
import { useRovingTabIndex } from '@/hooks/ui/useRovingTabIndex';

const HIGHLIGHT_DURATION_MS = 1400;

const FONT_WEIGHT_REGULAR = 400;
const FONT_WEIGHT_SEMIBOLD = 600;

export default function FilterBar() {
  const t = useT();
  const { pathname } = useLocation();
  const {
    allTasks,
    taskTree,
    tasksMap,
    projectFilter,
    contextFilter,
    setProjectFilter,
    setContextFilter,
    projectDisplayMap,
    contextDisplayMap,
  } = useTasksContext();

  const rootRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    const onHighlight = () => {
      rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      setHighlighted(true);
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(
        () => setHighlighted(false),
        HIGHLIGHT_DURATION_MS,
      );
    };
    window.addEventListener(HIGHLIGHT_PROJECT_FILTER_EVENT, onHighlight);
    return () => {
      window.removeEventListener(HIGHLIGHT_PROJECT_FILTER_EVENT, onHighlight);
      clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const view = getViewFromPath(pathname);
  const scopedTasks = useMemo(
    () => getViewScopedTasks(allTasks, view, { taskTree, tasksMap }),
    [allTasks, view, taskTree, tasksMap],
  );

  const projectOptions = useMemo(
    () =>
      ensureSelectedOptions(
        buildTagOptions(scopedTasks, projectDisplayMap, getTaskProjects),
        projectFilter,
        projectDisplayMap,
      ),
    [scopedTasks, projectDisplayMap, projectFilter],
  );
  const contextOptions = useMemo(
    () =>
      ensureSelectedOptions(
        buildTagOptions(scopedTasks, contextDisplayMap, getTaskContexts),
        contextFilter,
        contextDisplayMap,
      ),
    [scopedTasks, contextDisplayMap, contextFilter],
  );

  const { selectedProjects, selectedContexts } = useMemo(
    () => buildSelectedFilterSets(projectFilter, contextFilter),
    [projectFilter, contextFilter],
  );
  const hasFilter = selectedProjects.size > 0 || selectedContexts.size > 0;

  const clearAllFilters = useCallback(() => {
    setProjectFilter([]);
    setContextFilter([]);
  }, [setProjectFilter, setContextFilter]);

  const chipActions = useMemo(() => {
    const actions = [{ key: 'all', activate: clearAllFilters }];
    for (const option of projectOptions) {
      actions.push({
        key: `project-${option.id}`,
        activate: () =>
          toggleFilterById(option.id, selectedProjects, setProjectFilter),
      });
    }
    for (const option of contextOptions) {
      actions.push({
        key: `context-${option.id}`,
        activate: () =>
          toggleFilterById(option.id, selectedContexts, setContextFilter),
      });
    }
    return actions;
  }, [
    clearAllFilters,
    projectOptions,
    contextOptions,
    selectedProjects,
    selectedContexts,
    setProjectFilter,
    setContextFilter,
  ]);

  const activateChip = useCallback(
    (index) => {
      chipActions[index]?.activate();
    },
    [chipActions],
  );

  const { onKeyDown, getTabIndex } = useRovingTabIndex(
    chipActions.length,
    activateChip,
  );

  const chipIndex = { current: 0 };
  const allChipIndex = chipIndex.current;
  chipIndex.current += 1;

  return (
    <Box
      ref={rootRef}
      data-testid="filterbar-root"
      data-highlighted={highlighted}
      role="toolbar"
      aria-label={t('filterbar.toolbar', 'Task filters')}
      onKeyDown={onKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexWrap: 'wrap',
        borderRadius: 1,
        transition: 'background-color 0.3s',
        bgcolor: highlighted ? 'warning.light' : 'transparent',
      }}
    >
      <Chip
        label={t('filterbar.all')}
        aria-label={t('filterbar.all')}
        size="small"
        variant={hasFilter ? 'outlined' : 'filled'}
        color={hasFilter ? 'default' : 'primary'}
        onClick={clearAllFilters}
        tabIndex={getTabIndex(allChipIndex)}
        sx={{
          fontWeight: hasFilter ? FONT_WEIGHT_REGULAR : FONT_WEIGHT_SEMIBOLD,
        }}
      />
      {projectOptions.map((option) =>
        renderProjectFilterChip({
          option,
          chipIndex,
          selectedProjects,
          setProjectFilter,
          getTabIndex,
        }),
      )}
      {projectOptions.length > 0 && contextOptions.length > 0
        ? renderFilterBarTagDivider()
        : null}
      {contextOptions.map((option) =>
        renderContextFilterChip({
          option,
          chipIndex,
          selectedContexts,
          setContextFilter,
          getTabIndex,
        }),
      )}
    </Box>
  );
}
